import React, { useEffect, useRef, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { InactiveDialog } from "..";

type ThreeState = boolean | null;

interface Props {
  state: ThreeState;
  onChange?: (newState: ThreeState) => void;
  onConfirmChange?: (newState: ThreeState) => void;
  isConfirmationOnTrue?: boolean;
  isConfirmationOnFalse?: boolean;
  isConfirmationOnNull?: boolean;
  confirmMessageTrue?: string;
  confirmMessageFalse?: string;
  confirmMessageNull?: string;
  confirmTitleTrue?: string;
  confirmTitleFalse?: string;
  confirmTitleNull?: string;
  IsReadOnly?: boolean;
  ReadonlyTitle?: string;
  Title?: string;
}

const ThreeStateSwitch: React.FC<Props> = ({
  state,
  onChange,
  onConfirmChange,
  isConfirmationOnTrue,
  isConfirmationOnFalse,
  isConfirmationOnNull,
  confirmMessageTrue,
  confirmMessageFalse,
  confirmMessageNull,
  confirmTitleTrue,
  confirmTitleFalse,
  confirmTitleNull,
  IsReadOnly,
  ReadonlyTitle,
  Title,
}) => {
  const [currentState, setCurrentState] = useState<ThreeState>(state);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingState, setPendingState] = useState<ThreeState | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentState(state);
  }, [state]);

  const getStateFromClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const bounds = trackRef.current?.getBoundingClientRect();
    if (!bounds) return null;

    const clickX = e.clientX - bounds.left;
    const third = bounds.width / 3;

    if (clickX < third) return false;
    if (clickX > 2 * third) return true;
    return null;
  };

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (IsReadOnly) return;

    const next = getStateFromClick(e);
    if (next === currentState) return;

    const needsConfirmation =
      (next === true && isConfirmationOnTrue) ||
      (next === false && isConfirmationOnFalse) ||
      (next === null && isConfirmationOnNull);

    if (needsConfirmation) {
      setPendingState(next);
      setDialogOpen(true);
    } else {
      applyStateChange(next);
    }
  };

  const applyStateChange = (newState: ThreeState) => {
    setCurrentState(newState);
    onChange?.(newState);
    onConfirmChange?.(newState);
  };

  const onClickDialogClose = () => {
    setDialogOpen(false);
    setPendingState(null);
  };

  const handleConfirm = () => {
    if (pendingState !== undefined) {
      applyStateChange(pendingState);
    }
    setDialogOpen(false);
    setPendingState(null);
  };

  const getTooltip = () => {
    if (IsReadOnly) return ReadonlyTitle || "";
    return Title || "";
  };

  const getConfirmTitle = () => {
    if (pendingState === true) return confirmTitleTrue || "";
    if (pendingState === false) return confirmTitleFalse || "";
    return confirmTitleNull || "";
  };

  const getConfirmMessage = () => {
    if (pendingState === true) return confirmMessageTrue || "";
    if (pendingState === false) return confirmMessageFalse || "";
    return confirmMessageNull || "";
  };

  const getThumbPosition = () => {
    if (currentState === false) return "4%";
    if (currentState === null) return "calc(50% - 10px)";
    return "calc(100% - 24px)";
  };

  const getThumbColor = () => {
    if (currentState === true) return "#ffffff"; 
    if (currentState === false) return "#ffffff"; 
    return "#ffffff"; 
  };

  return (
    <Box display="flex" alignItems="center">
      <Tooltip title={getTooltip()}>
        <div
          ref={trackRef}
          onClick={handleTrackClick}
          style={{
            width: 60,
            height: 26,
            background: "rgb(187 187 187)",
            borderRadius: 14,
            position: "relative",
            cursor: IsReadOnly ? "not-allowed" : "pointer",
            opacity: IsReadOnly ? 0.6 : 1,
          }}
        >
          <div
            style={{
              width: 22,
              height: 22,
              background: getThumbColor(),
              borderRadius: "50%",
              position: "absolute",
              top: 2,
              left: getThumbPosition(),
              transition: "left 0.2s ease-in-out",
              boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
            }}
          />
        </div>
      </Tooltip>

      <InactiveDialog
        open={dialogOpen}
        onClose={onClickDialogClose}
        onConfirm={handleConfirm}
        title={getConfirmTitle()}
        message={getConfirmMessage()}
      />
    </Box>
  );
};

export default ThreeStateSwitch;
